/**
 * The citizen's profile fields — the rule for each, in one place.
 * (F33 · Citizen first run · Lucas's A2 ④)
 *
 * The CheckUp score needs six things (name, date of birth, nationality, the
 * country and city they live in, a phone). The rules for what makes each one
 * valid lived inside journey-web's onboarding wizard as imperative checks; the
 * focused "complete your profile" form (web), the mobile profile editor and the
 * API all need the SAME rule, so it lives here as schemas.
 *
 * Messages are i18n keys — mostly the wizard's own, already translated in five
 * locales. Two carry a number the client supplies when rendering
 * (`{{minAge}}`, `{{minDigits}}` / `{{maxDigits}}`); the constants below are
 * exported for exactly that.
 */
import { z } from 'zod';

import { countryCodeSchema } from '../validators';
import { CITY_MAX_LENGTH } from '../onboarding/citizen-setup';

export const PROFILE_MIN_AGE = 16;
export const PROFILE_MAX_AGE = 100;
export const PHONE_DIGITS_MIN = 6;
export const PHONE_DIGITS_MAX = 15;
const PHONE_PATTERN = /^[+\d\s\-().]+$/;

/** Whole years between an ISO date (YYYY-MM-DD) and today; NaN when unparseable. */
export function ageFromIsoDate(iso: string, today: Date = new Date()): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return Number.NaN;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const born = new Date(Date.UTC(year, month - 1, day));
  if (born.getUTCMonth() !== month - 1 || born.getUTCDate() !== day) return Number.NaN;
  let age = today.getUTCFullYear() - year;
  const beforeBirthday =
    today.getUTCMonth() < month - 1 ||
    (today.getUTCMonth() === month - 1 && today.getUTCDate() < day);
  if (beforeBirthday) age -= 1;
  return age;
}

const digitCount = (value: string) => value.replace(/\D/g, '').length;

/** One schema per input the profile form can render. */
export const profileFieldSchemas = {
  firstName: z
    .string()
    .trim()
    .min(1, 'onboarding.validation.firstName_required')
    .max(100, 'onboarding.validation.firstName_max_length'),
  lastName: z
    .string()
    .trim()
    .min(1, 'onboarding.validation.lastName_required')
    .max(100, 'onboarding.validation.lastName_max_length'),
  dateOfBirth: z
    .string()
    .min(1, 'onboarding.validation.dateOfBirth_required')
    .refine((value) => !Number.isNaN(ageFromIsoDate(value)), {
      message: 'onboarding.validation.dateOfBirth_invalid_format',
    })
    .refine((value) => !(ageFromIsoDate(value) < PROFILE_MIN_AGE), {
      message: 'onboarding.validation.dateOfBirth_min_age',
    })
    .refine((value) => !(ageFromIsoDate(value) > PROFILE_MAX_AGE), {
      message: 'onboarding.validation.dateOfBirth_invalid',
    }),
  /**
   * Every country the citizen holds citizenship of — the one list the profile
   * keeps (`nationalities`, D-22), never a single "nationality": a person with
   * two passports used to be able to give only one here.
   */
  citizenships: z
    .array(countryCodeSchema)
    .min(1, 'onboarding.validation.citizenship_required')
    .refine((codes) => new Set(codes).size === codes.length, {
      message: 'onboarding.validation.citizenship_required',
    }),
  currentCountry: z.string().min(1, 'validation.originCountry.required').pipe(countryCodeSchema),
  currentCity: z
    .string()
    .trim()
    .min(1, 'validation.city.required')
    .max(CITY_MAX_LENGTH, 'validation.city.tooLong'),
  phone: z
    .string()
    .trim()
    .min(1, 'onboarding.validation.phone_required')
    .regex(PHONE_PATTERN, 'onboarding.validation.phone_invalid_characters')
    .refine((value) => digitCount(value) >= PHONE_DIGITS_MIN, {
      message: 'onboarding.validation.phone_min_digits',
    })
    .refine((value) => digitCount(value) <= PHONE_DIGITS_MAX, {
      message: 'onboarding.validation.phone_max_digits',
    }),
  /** The dial code's country (ISO alpha-2). Optional: a phone may carry its own `+code`. */
  phoneCountryCode: countryCodeSchema.optional(),
} as const;

export type ProfileFieldInput = keyof typeof profileFieldSchemas;

/**
 * A schema for exactly the inputs a form renders — the focused form asks only
 * for what is missing, so it validates only that.
 */
export function profileFieldsSchema(inputs: readonly ProfileFieldInput[]) {
  const shape: Partial<Record<ProfileFieldInput, z.ZodType>> = {};
  for (const input of inputs) shape[input] = profileFieldSchemas[input];
  return z.object(shape as Record<string, z.ZodType>);
}

export type ProfileFieldsInput = Partial<{
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  citizenships: string[];
  currentCountry: string;
  currentCity: string;
  phone: string;
  phoneCountryCode: string;
}>;
